/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { Route, Routes } from 'react-router-dom';

import { isLocalDevelopment } from '@globalUtils/environment';

import './i18n';

import '@assets/styles.css';

import { WhatsAppFloat } from '@components/whatsappFloat';

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('@pages/home'));
const ContactPage = React.lazy(() => import('@pages/contact'));
const PrivacyPolicyPage = React.lazy(() => import('@pages/privacyPolicy'));
const TermsConditionsPage = React.lazy(() => import('@pages/termsConditions'));
const AuditPage = React.lazy(() => import('@pages/audit'));

// Defer analytics initialization to reduce main thread work
const initAnalytics = () => {
  if (isLocalDevelopment) return;

  const init = async () => {
    const [{ default: mixpanel }] = await Promise.all([import('mixpanel-browser')]);

    mixpanel.init('53a910efa28f235f725395ca0785ce3d', {
      debug: false,
      track_pageview: true,
      persistence: 'localStorage',
      record_sessions_percent: 10, // Reduced from 100%
      record_heatmap_data: false, // Disabled - expensive
      record_idle_timeout_ms: 1000 * 60 * 5,
      record_mask_text_selector: '',
      record_block_selector: '',
      autocapture: {
        pageview: 'full-url',
        click: false, // Disabled - expensive
        input: true,
        scroll: false, // Disabled - expensive
        submit: true,
        capture_text_content: false,
      },
    } as any);
  };

  // Use requestIdleCallback to defer initialization
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => init(), { timeout: 3000 });
  } else {
    setTimeout(init, 2000);
  }
};

// Initialize analytics after first render
initAnalytics();

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="w-full h-screen flex items-center justify-center">
    <div className="animate-pulse text-gray-400"></div>
  </div>
);

const App = () => {
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    setTimeout(() => {
      document.dispatchEvent(new Event('prerender-ready'));
    }, 10000);
  }, []);

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsConditionsPage />} />
          <Route path="/audit" element={<AuditPage />} />
        </Routes>
      </Suspense>
      <WhatsAppFloat />
    </Router>
  );
};

const container = document.getElementById('root') || document.body;
const root = createRoot(container);
root.render(<App />);
